import { Divider } from '@vertex-protocol/web-ui';

interface Props {
  /** Bridge fee — either raw ETH value (e.g. "0.0003") or pre-formatted USDC (e.g. "~$0.001 USDC") or "Free (CCTP)" */
  lzFee: string;
  /** Gas airdrop amount in ETH, or undefined if not applicable */
  gasAirdropAmount: string | undefined;
  /** Final amount user will receive */
  estimatedReceiveAmount: string;
  /** Token symbol for the receive amount */
  tokenSymbol: string;
  /** Bridge provider — determines fee display format */
  bridgeProvider?: string;
  /** Across output amount from the live quote */
  acrossExpectedOutput?: string;
  /** Across fill estimate in seconds */
  acrossFillEstimateSec?: number;
}

/**
 * Fee breakdown shown for cross-chain (bridge) deposits.
 * Displays the LayerZero messaging fee, optional gas airdrop cost,
 * and the final receive amount after deductions.
 */
export function NadoFeeSummary({
  lzFee,
  gasAirdropAmount,
  estimatedReceiveAmount,
  tokenSymbol,
  bridgeProvider,
  acrossExpectedOutput,
  acrossFillEstimateSec,
}: Props) {
  // Format bridge fee based on provider
  const bridgeFeeDisplay = (() => {
    if (lzFee === 'Loading...') return lzFee;
    if (bridgeProvider === 'across') return lzFee; // Already formatted as "~$X USDC"
    if (bridgeProvider === 'cctp') return 'Free (CCTP)';
    // OFT/Stargate: raw ETH value
    return `~${lzFee} ETH`;
  })();

  return (
    <div className="flex flex-col gap-y-1.5">
      <FeeRow
        label="Bridge Fee"
        value={bridgeFeeDisplay}
      />
      {bridgeProvider === 'across' && acrossExpectedOutput && (
        <FeeRow
          label="Across Output"
          value={`~${acrossExpectedOutput} ${tokenSymbol}`}
        />
      )}
      {bridgeProvider === 'across' && acrossFillEstimateSec !== undefined && (
        <FeeRow
          label="Fill Estimate"
          value={`~${acrossFillEstimateSec}s`}
        />
      )}
      {gasAirdropAmount && (
        <FeeRow
          label="Gas airdrop"
          value={`~${gasAirdropAmount} ETH`}
          hint="Covers your first transactions on Ink"
        />
      )}
      <Divider className="my-0.5" />
      <FeeRow
        label="You will receive"
        value={`~${estimatedReceiveAmount} ${tokenSymbol}`}
        isHighlighted
      />
    </div>
  );
}

interface FeeRowProps {
  label: string;
  value: string;
  hint?: string;
  isHighlighted?: boolean;
}

function FeeRow({ label, value, hint, isHighlighted }: FeeRowProps) {
  return (
    <div className="flex items-start justify-between gap-x-4">
      <div className="flex flex-col">
        <span className="text-text-secondary text-xs">{label}</span>
        {hint && <span className="text-text-tertiary text-2xs">{hint}</span>}
      </div>
      <span
        className={
          isHighlighted
            ? 'text-text-primary text-sm font-medium'
            : 'text-text-secondary text-xs'
        }
      >
        {value}
      </span>
    </div>
  );
}
